import { Department, Follow, Product } from '@app/models';
import {
  getAncestorsIndexUpToParentId,
  getAvailableChildren
} from '@app/models/department';
import * as fromFilters from '@core/reducers/filter.reducer';
import * as fromProducts from '@core/reducers/product.reducer';
import * as fromRoot from '@core/reducers/root.reducer';
import * as fromSelectedFollow from '@app/modules/follow/reducers/follow.reducer';
import {
  FollowDetailPageActionsUnion,
  FollowDetailPageActionTypes
} from '@follow-detail/actions/follow-detail.actions';
import { FollowDetailModuleState } from '@follow-detail/reducers';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as rolesUtils from '@app/role.utils';

export interface State {
  selectedProductIds: number[];
  filter: string;
  reset: boolean;
}

export const INITIAL_STATE: State = {
  selectedProductIds: [],
  filter: null,
  reset: true
};

export function reducer(
  state: State = INITIAL_STATE,
  action: FollowDetailPageActionsUnion
) {
  switch (action.type) {
    case FollowDetailPageActionTypes.SelectProducts:
      return { ...state, selectedProductIds: action.ids };
    case FollowDetailPageActionTypes.SetFilter:
      return { ...state, filter: action.filter };

    case FollowDetailPageActionTypes.ResetData:
      return { ...state, reset: action.reset };
    default:
      return state;
  }
}

const getModule = createFeatureSelector('followDetailModule');

const getFollowDetailPageState = createSelector(
  getModule,
  (state: FollowDetailModuleState) => state.followDetailPage
);

export const selectProductIds = (state: State) => state.selectedProductIds;
export const selectFilter = (state: State) => state.filter;
export const selectReset = (state: State) => state.reset;

export const getProductIds = createSelector(
  getFollowDetailPageState,
  selectProductIds
);

export const getFilter = createSelector(
  getFollowDetailPageState,
  selectFilter
);

export const getReset = createSelector(
  getFollowDetailPageState,
  selectReset
);

const getFollowType = (department, entities) => {
  if (department && department.ParentId) {
    return getFollowType(entities[department.ParentId], entities);
  } else {
    return department && department.type;
  }
};

const populateProducts = (
  products,
  departmentEntities,
  brandEntities,
  providerEntities
) => {
  return products
    ? products.map(p => {
        let parent;
        let grandParent;
        const department = departmentEntities[p.DepartmentId];
        if (department) {
          parent = departmentEntities[department.ParentId];
        }

        if (parent) {
          grandParent = departmentEntities[parent.ParentId];
        }

        const brand = brandEntities[p.BrandId];
        const Provider = providerEntities[p.ProviderId];
        return {
          ...p,
          Brand: brand,
          brandName: (brand && brand.name) || 'Desconocido',
          Department: department,
          Provider,
          departmentName: (department && department.name) || 'Desconocido',
          parentName: (parent && parent.name) || 'Desconocido',
          grandParentName: (grandParent && grandParent.name) || 'Desconocido'
        };
      })
    : [];
};

export const getFollowProducts = createSelector(
  fromSelectedFollow.getProducts,
  fromRoot.getDepartmentEntities,
  fromRoot.getBrandEntities,
  fromRoot.getProviderEntities,
  (products, departmentEntities, brandEntities, providerEntities) => {
    return products
      ? populateProducts(
          products,
          departmentEntities,
          brandEntities,
          providerEntities
        ).reduce((acc, product, idx, arr) => {
          if (product && product.setId === null) {
            const components = arr.filter(p => p.setId === product.id);
            // console.log('-sdas', components);
            if (components) {
              product.components = components;
            } else {
              product.components = [];
            }
          }
          // }
          return [...acc, product];
        }, [])
      : [];
  }
);

export const hasProducts = createSelector(
  getFollowProducts,
  (products: Product[]) => {
    return products && products.length;
  }
);

const getValues = value => {
  if (Array.isArray(value)) {
    return value.join(' ');
  } else if (value !== null && typeof value === 'object') {
    return Object.keys(value)
      .map(key => getValues(value[key]))
      .join(' ')
      .toLowerCase();
  } else {
    return value;
  }
};

const createSearchIndexes = items => {
  return items.map(item => ({
    ...item,
    index: Object.keys(item)
      .map(key => getValues(item[key]))
      .join(' ')
      .toLowerCase()
  }));
};

export const getFollowProductsFiltered = createSelector(
  getFollowProducts,
  fromProducts.getFilteredProductIds,
  (products, filteredProductsIds) => {
    //if (products && filteredProductsIds) {
    //return products.filter(p => filteredProductsIds.includes(p.id));
    //}

    if (filteredProductsIds.length) {
      products = products.filter(p => filteredProductsIds.includes(p.id));
    }

    return products;

    //const filters = filter
    //? filter
    //.toLowerCase()
    //.split(',')
    //.map(f => f.trim())
    //: [];

    //return filters && products && advancedFilters
    //? createSearchIndexes(products).filter(
    //p =>
    //advancedFilters.every(f => p[f.key] === f.value) &&
    //filters.every(f => p.index.includes(f))
    //)
    //: products;
  }
);

export const getDrillDown = createSelector(
  getFollowProductsFiltered,
  // fromProducts.getFilteredProductIds,
  products => {
    if (products) {
      // if (filteredProductIds.length) {
      //   products = products.filter(p => filteredProductIds.includes(p.id));
      // }

      const drillDown = products.reduce((obj, product) => {
        const depName = product.departmentName;
        const parentName = product.parentName;
        const grandParentName = product.grandParentName;
        let groupName;

        if (product.description) {
          if (product.mainCode) {
            groupName = `${product.description} (${product.mainCode})`;
          } else {
            groupName = `${product.description} (Sin código madre)`;
          }
        } else {
          if (product.mainCode) {
            groupName = product.mainCode;
          } else {
            groupName = 'Sin código madre';
          }
        }

        if (obj[grandParentName]) {
          if (obj[grandParentName][parentName]) {
            if (obj[grandParentName][parentName][depName]) {
              if (obj[grandParentName][parentName][depName][groupName]) {
                obj[grandParentName][parentName][depName][groupName] = [
                  ...obj[grandParentName][parentName][depName][groupName],
                  product
                ];
              } else {
                obj[grandParentName][parentName][depName][groupName] = [
                  product
                ];
              }
            } else {
              obj[grandParentName][parentName][depName] = {};
              obj[grandParentName][parentName][depName][groupName] = [product];
            }
          } else {
            obj[grandParentName][parentName] = {};
            obj[grandParentName][parentName][depName] = {};
            obj[grandParentName][parentName][depName][groupName] = [product];
          }
        } else {
          obj[grandParentName] = {};
          obj[grandParentName][parentName] = {};
          obj[grandParentName][parentName][depName] = {};
          obj[grandParentName][parentName][depName][groupName] = [product];
        }

        //
        return obj;
      }, {});

      return drillDown;
    }
  }
);

export const getSortedDrillDownAndProducts = createSelector(
  getDrillDown,
  drillDown => {
    const sortedDrillDown = {};
    let sortedItems = [];
    // let parents = Object.keys(drillDown).sort((a, b) => a.localeCompare(b));
    const l3s = Object.keys(drillDown).sort((a, b) => a.localeCompare(b));

    l3s.forEach(l3 => {
      if (!sortedDrillDown[l3]) {
        sortedDrillDown[l3] = {};
      }
      const parents = Object.keys(drillDown[l3]).sort((a, b) =>
        a.localeCompare(b)
      );
      parents.forEach(p => {
        if (!sortedDrillDown[l3][p]) {
          sortedDrillDown[l3][p] = {};
        }

        const deps = Object.keys(drillDown[l3][p]).sort((a, b) =>
          a.localeCompare(b)
        );

        deps.forEach(d => { 
          if (!sortedDrillDown[l3][p][d]) {
            sortedDrillDown[l3][p][d] = [];
          }
          const mainCodes = Object.keys(drillDown[l3][p][d]).sort((a, b) =>
            a.localeCompare(b)
          );
          mainCodes.forEach(mainCode => {
            if (!sortedDrillDown[l3][p][d][mainCode]) {
              sortedDrillDown[l3][p][d][mainCode] = [];
            }
            const codes = drillDown[l3][p][d][mainCode].sort((a, b) =>
              a.code.localeCompare(b.code)
            );
            codes.forEach(code => {
              sortedItems = [...sortedItems, code];
              sortedDrillDown[l3][p][d][mainCode] = [
                ...sortedDrillDown[l3][p][d][mainCode],
                code
              ];
            });
          });
        });
      });
    });

    return [sortedDrillDown, sortedItems];
  }
);

export const getSortedDrillDown = createSelector(
  getSortedDrillDownAndProducts,
  drill => drill[0]
);

export const getSortedProducts = createSelector(
  getSortedDrillDownAndProducts,
  drill => drill[1]
);

export const getSelectedProducts = createSelector(
  getFollowProducts,
  getProductIds,
  (products, ids) => {
    return products && ids && ids.map(id => products.find(p => p.id === id));
  }
);

export const getFirstSelectedProduct = createSelector(
  getSelectedProducts,
  products => {
    return products && products.length ? products[0] : null;
  }
);

export const getFirstSelectedProductComments = createSelector(
  getFirstSelectedProduct,
  fromRoot.getCommentsPopulated,
  (product, comments) => {
    return product
      ? comments.filter(
          c => c.CommentableType === 'Product' && c.CommentableId === product.id
        )
      : [];
  }
);

export const getSelectedProductCountries = createSelector(
  getProductIds,
  fromSelectedFollow.getPopulated,
  fromRoot.getProductCountriesByProductId,
  fromRoot.getCountryEntities,
  fromRoot.getMe,
  (ids, follow: Follow, countriesByProduct, countriesEntities, me) => {
    const countries = ids && ids.length && countriesByProduct[ids[0]];
    if (countries && me) {
      return countries
        .map(country => {
          const tax = follow.Taxes.find(x => x.CountryId === country.CountryId);
          const factor = follow.Factors.find(
            x => x.CountryId === country.CountryId
          );
          const rate = follow.ExchangeRates.find(
            x => x.CountryId === country.CountryId
          );
          return {
            ...country,
            factor: country.factor ? parseFloat(country.factor) : 0,
            followCountryExchangeRate: rate ? parseFloat(rate.value) : 0,
            followCountryTax: tax ? parseFloat(tax.value) : 0,
            followCountryFactor: factor ? parseFloat(factor.value) : 0,
            Country: countriesEntities[country.CountryId]
          };
        })
        .filter(c =>
          // me.role !== 'country-buyer' 
         !rolesUtils.isCountryUser(me.role) ? true : c.Country.id === me.CountryId
        );
    } else {
      return [];
    }
  }
);

export const getFollowProductCountries = createSelector(
  getFollowProducts,
  fromSelectedFollow.getPopulated,
  fromRoot.getProductCountriesByProductId,
  fromRoot.getCountryEntities,
  fromRoot.getMe,
  (products, follow: Follow, countriesByProduct, countriesEntities, me) => {
    const ids = products && products.map(p => p.id);
    const countries = ids && ids.length && countriesByProduct[ids[0]];
    if (countries && me) {
      return countries
        .map(country => {
          const tax = follow.Taxes.find(x => x.CountryId === country.CountryId);
          const factor = follow.Factors.find(
            x => x.CountryId === country.CountryId
          );
          const rate = follow.ExchangeRates.find(
            x => x.CountryId === country.CountryId
          );
          return {
            ...country,
            factor: country.factor ? parseFloat(country.factor) : 0,
            followCountryExchangeRate: rate ? parseFloat(rate.value) : 0,
            followCountryTax: tax ? parseFloat(tax.value) : 0,
            followCountryFactor: factor ? parseFloat(factor.value) : 0,
            Country: countriesEntities[country.CountryId]
          };
        })
        .filter(c =>
          // me.role !== 'country-buyer' 
          !rolesUtils.isCountryUser(me.role) ? true : c.Country.id === me.CountryId
        );
    } else {
      return [];
    }
  }
);

export const getAvailableSubDepartments = createSelector(
  fromRoot.getSelectedDepartment,
  fromSelectedFollow.getPopulated,
  fromRoot.getDepartments,
  (department, follow, departments) => {
    if (department && follow) {
      const children = getAvailableChildren(
        follow.plannerId,
        [department],
        departments,
        []
      );
      return children;
    } else {
      return [];
    }
  }
);

export const getLeaves = createSelector(
  getAvailableSubDepartments,
  fromSelectedFollow.getPopulated,
  (departments, follow) =>
    departments
      .filter(
        department =>
          department.level === 5 &&
          department.plannerIds.includes(follow.plannerId)
      )
      .map(d => ({ ...d, codeName: `${d.jpId} - ${d.name}` }))
      .sort((a, b) => a.codeName.localeCompare(b.codeName))
);

export const getIndexedSubDepartments = createSelector(
  getAvailableSubDepartments,
  fromRoot.getDepartmentEntities,
  fromRoot.getSelectedDepartmentId,
  (departments: Department[], entities, id) => {
    return departments
      .sort((a, b) => {
        return a.level - b.level;
      })
      .map(department => {
        const res = {
          ...department,
          indexed: getAncestorsIndexUpToParentId(department, entities, '', id)
        };
        return res;
      });
  }
);

export const getBusy = createSelector(
  fromRoot.getFollowProductsLoaded,
  fromSelectedFollow.getPopulated,
  (loaded, follow) => (follow ? !loaded.includes(follow.id) : true)
);

export const getDrillDownFood = createSelector(
  getFollowProductsFiltered,
  fromRoot.getMe,
  fromRoot.getDepartmentFoodEntities,
  // getFollowProductCountries,
  // (products, productCountries) => {
  (products, me, countryDepartment) => {
    let parent;
    let grandParent;
    let grandGrandParent;
    let articleGroup;
    let parentName;
    let grandParentName;
    if (products) {
      const drillDown = products
        .sort((a, b) => a.code.localeCompare(b.code))
        .reduce((obj, product) => {

          if(product && product.DepartmentsFoodId){
            const departmentN1 = countryDepartment[product.DepartmentsFoodId];

            if(departmentN1){
              articleGroup = departmentN1.name;
              parent = countryDepartment[departmentN1.ParentId];
            }

            if(parent){
              parentName = parent.name;
              grandParent = countryDepartment[parent.ParentId];
            }

            if(grandParent){
              grandParentName = grandParent.name;
              grandGrandParent = countryDepartment[grandParent.ParentId];
            }

            if (parentName && articleGroup && grandParent) {
              if (obj[grandParentName]) {
                if (obj[grandParentName][parentName]) {
                  if (obj[grandParentName][parentName][articleGroup]) {
                    obj[grandParentName][parentName][articleGroup] = [
                      ...obj[grandParentName][parentName][articleGroup],
                      product
                    ];
                  } else {
                    obj[grandParentName][parentName][articleGroup] = [product];
                  }
                } else {
                  obj[grandParentName][parentName] = {};
                  obj[grandParentName][parentName][articleGroup] = [product];
                }
              } else {
                obj[grandParentName] = {};
                obj[grandParentName][parentName] = {};
                obj[grandParentName][parentName][articleGroup] = [product];
              }
            } else {
              if (obj['Sin grupo de artículos']) {
                obj['Sin grupo de artículos'] = [
                  ...obj['Sin grupo de artículos'],
                  product
                ];
              } else {
                obj['Sin grupo de artículos'] = [product];
              }
            }
          }else {
            if (obj['Sin grupo de artículos']) {
              obj['Sin grupo de artículos'] = [
                ...obj['Sin grupo de artículos'],
                product
              ];
            } else {
              obj['Sin grupo de artículos'] = [product];
            }
          }

          return obj;
        }, {});

      return drillDown;
    }
  }
);

export const getCountryDrillDown = createSelector(
  getFollowProductsFiltered,
  fromRoot.getMe,
  fromRoot.getCountryDepartmentEntities,
  // getFollowProductCountries,
  // (products, productCountries) => {
  (products, me, countryDepartment) => {
    let parent;
    let grandParent;
    let grandGrandParent;
    let articleGroup;
    let parentName;
    let grandParentName;
    if (products) {
      const drillDown = products
        .sort((a, b) => a.code.localeCompare(b.code))
        .reduce((obj, product) => {
          const productCountry =
            product &&
            product.ProductCountries.find(
              pc =>
                pc.CountryId === me.CountryId &&
                pc.businessUnit === me.businessUnit
            );

          if (productCountry && productCountry.CountryDepartmentId) {
            const countryDepartmentN1 =
              countryDepartment[productCountry.CountryDepartmentId];

            if (countryDepartmentN1) {
              articleGroup = countryDepartmentN1.name;
              parent = countryDepartment[countryDepartmentN1.ParentId];
            }

            if (parent) {
              parentName = parent.name;
              grandParent = countryDepartment[parent.ParentId];
            }

            if (grandParent) {
              grandParentName = grandParent.name;
              grandGrandParent = countryDepartment[grandParent.ParentId];
            }

            const grandGrandParentName = grandGrandParent
              ? grandGrandParent.name
              : 'Sin grupo de artículos';
            if (parentName && articleGroup && grandParent) {
              if (obj[grandParentName]) {
                if (obj[grandParentName][parentName]) {
                  if (obj[grandParentName][parentName][articleGroup]) {
                    obj[grandParentName][parentName][articleGroup] = [
                      ...obj[grandParentName][parentName][articleGroup],
                      product
                    ];
                  } else {
                    obj[grandParentName][parentName][articleGroup] = [product];
                  }
                } else {
                  obj[grandParentName][parentName] = {};
                  obj[grandParentName][parentName][articleGroup] = [product];
                }
              } else {
                obj[grandParentName] = {};
                obj[grandParentName][parentName] = {};
                obj[grandParentName][parentName][articleGroup] = [product];
              }
            } else {
              if (obj['Sin grupo de artículos']) {
                obj['Sin grupo de artículos'] = [
                  ...obj['Sin grupo de artículos'],
                  product
                ];
              } else {
                obj['Sin grupo de artículos'] = [product];
              }
            }
          } else {
            if (obj['Sin grupo de artículos']) {
              obj['Sin grupo de artículos'] = [
                ...obj['Sin grupo de artículos'],
                product
              ];
            } else {
              obj['Sin grupo de artículos'] = [product];
            }
          }

          return obj;
        }, {});

      return drillDown;
    }
  }
);

export const getCountrySortedDrillDownAndProducts = createSelector(
  getCountryDrillDown,
  drillDown => {
    const sortedDrillDown = {};
    let sortedItems = [];
    const keys = Object.keys(drillDown).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    keys.forEach(k => {
      const keyTwo = Object.keys(drillDown[k]).sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );

      const parseKeyTwo = parseInt(keyTwo[0], 10);

      if (!Number.isInteger(parseKeyTwo)) {
        keyTwo.forEach(async k2 => {
          const keyThree = Object.keys(drillDown[k][k2]).sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
          );

          keyThree.forEach(async k3 => {
            sortedItems = [...sortedItems, ...drillDown[k][k2][k3]];
          });
        });
      } else {
        if (drillDown['Sin grupo de artículos']) {
          sortedItems = [
            ...sortedItems,
            ...drillDown['Sin grupo de artículos']
          ];
        }
      }

      sortedDrillDown[k] = drillDown[k];
    });
    return [sortedDrillDown, sortedItems];
  }
);

export const getCountrySortedDrillDownAndProductsForFood = createSelector(
  getDrillDownFood,
  drillDown => {
    const sortedDrillDown = {};
    let sortedItems = [];
    const keys = Object.keys(drillDown).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    keys.forEach(k => {
      const keyTwo = Object.keys(drillDown[k]).sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );

      const parseKeyTwo = parseInt(keyTwo[0], 10);

      if (!Number.isInteger(parseKeyTwo)) {
        keyTwo.forEach(async k2 => {
          const keyThree = Object.keys(drillDown[k][k2]).sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
          );

          keyThree.forEach(async k3 => {
            sortedItems = [...sortedItems, ...drillDown[k][k2][k3]];
          });
        });
      } else {
        if (drillDown['Sin grupo de artículos']) {
          sortedItems = [
            ...sortedItems,
            ...drillDown['Sin grupo de artículos']
          ];
        }
      }

      sortedDrillDown[k] = drillDown[k];
    });
    return [sortedDrillDown, sortedItems];
  }
);

export const getCountrySortedDrillDown = createSelector(
  getCountrySortedDrillDownAndProducts,
  drill => drill[0]
);

export const getCountrySortedProducts = createSelector(
  getCountrySortedDrillDownAndProducts,
  drill => drill[1]
);

export const getCountrySortedDrillDownForFood = createSelector(
  getCountrySortedDrillDownAndProductsForFood,
  drill => drill[0]
);

export const getCountrySortedProductsForFood = createSelector(
  getCountrySortedDrillDownAndProductsForFood,
  drill => drill[1]
);

export const getSortedDrillDownAndProductsForFood = createSelector(
  getDrillDownFood,
  drillDown => {
    const sortedDrillDown = {};
    let sortedItems = [];
    // let parents = Object.keys(drillDown).sort((a, b) => a.localeCompare(b));
    const l3s = Object.keys(drillDown).sort((a, b) => a.localeCompare(b));

    l3s.forEach(l3 => {
      if (!sortedDrillDown[l3]) {
        sortedDrillDown[l3] = {};
      }
      const parents = Object.keys(drillDown[l3]).sort((a, b) =>
        a.localeCompare(b)
      );

      parents.forEach(p => {
        if (!sortedDrillDown[l3][p]) {
          sortedDrillDown[l3][p] = [];
        }

        const mainCodes = Object.keys(drillDown[l3][p]).sort((a, b) =>
          a.localeCompare(b)
        );
        mainCodes.forEach(mainCode => {
          if (!sortedDrillDown[l3][p][mainCode]) {
            sortedDrillDown[l3][p][mainCode] = [];
          }
          const codes = drillDown[l3][p][mainCode].sort((a, b) =>
            a.code.localeCompare(b.code)
          );
          codes.forEach(code => {
            sortedItems = [...sortedItems, code];
            sortedDrillDown[l3][p][mainCode] = [
              ...sortedDrillDown[l3][p][mainCode],
              code
            ];
          });
        });
      });
    });

    return [sortedDrillDown, sortedItems];
  }
);

export const getSortedDrillDownForFood = createSelector(
  getSortedDrillDownAndProductsForFood,
  drill => drill[0]
);